/**
 * Track validity checks — used by unit tests for the hardcoded test track, by
 * the parametric generator to reject/regenerate invalid tracks, and by the
 * hand-drawing editor (live feedback with located issues) and the server
 * (authoritative re-check before accepting a drawn circuit).
 *
 * Together these checks make the no-wall-traversal collision invariant *provable*:
 *
 * 1. Clearance — for any point P inside the band of a section A (distance to A's
 *    centerline ≤ halfWidth_A), every arc-distant section B must appear strictly
 *    farther than A even through the coarse closest-point search (stride 4 ≈ 24 px,
 *    worst-case chord error ±12 px). dist(P,B) ≥ D − hw_A and dist(P,A) ≤ hw_A, so
 *    requiring D ≥ 2·max(hw_A, hw_B) + margin (margin > 24) guarantees the coarse
 *    search can never lock onto the wrong section, whatever the width difference.
 *
 * 2. Curvature — if the turn radius drops below the local half width, the inner
 *    edge folds over itself: the band self-overlaps *within* the arc-separation
 *    window that the clearance check deliberately exempts. Requiring
 *    radius ≥ halfWidth + curvatureMargin at every sample closes that gap.
 */

import type { Track, TrackSample } from './track';
import { forwardArcDelta } from './track';

export interface TrackValidationOptions {
  /** Arc separation (px) beyond which two samples must respect the clearance rule. */
  minArcSeparation: number;
  /** Extra euclidean margin (px) on top of 2·max(halfWidth) — keep > 24 (see above). */
  clearanceMargin: number;
  /** Narrowest allowed corridor (full width, px). */
  minWidth: number;
  /** Extra margin (px) required between turn radius and local half width. */
  curvatureMargin: number;
  /** Shortest allowed centerline (px). 0 = no constraint (generator default). */
  minLength?: number;
  /** Longest allowed centerline (px). Infinity = no constraint. */
  maxLength?: number;
}

export const DEFAULT_VALIDATION: TrackValidationOptions = {
  minArcSeparation: 250,
  clearanceMargin: 30,
  minWidth: 80,
  curvatureMargin: 12,
};

export type TrackIssueCode =
  'samples' | 'nonfinite' | 'narrow' | 'curvature' | 'clearance' | 'length';

/** One located validity problem — `s` (and `s2` for pairs) point at the spot. */
export interface TrackIssue {
  code: TrackIssueCode;
  message: string;
  /** Curvilinear abscissa of the problem, when it has one. */
  s?: number;
  /** Second abscissa for pairwise problems (clearance). */
  s2?: number;
}

export interface TrackValidationResult {
  valid: boolean;
  errors: string[];
  /** Same problems, structured and located (editor markers). */
  issues: TrackIssue[];
}

/** Issues collected per category before we stop looking (editor readability). */
const MAX_ISSUES_PER_KIND = 6;

export function validateTrack(
  track: Track,
  opts: TrackValidationOptions = DEFAULT_VALIDATION,
): TrackValidationResult {
  const issues: TrackIssue[] = [];
  const push = (issue: TrackIssue): void => {
    issues.push(issue);
  };
  const samples = track.samples;
  const count = samples.length;

  if (count < 64) {
    push({ code: 'samples', message: `too few samples: ${count}` });
  }

  const minLength = opts.minLength ?? 0;
  const maxLength = opts.maxLength ?? Infinity;
  if (track.length < minLength) {
    push({
      code: 'length',
      message: `track too short: ${track.length.toFixed(0)}px < ${minLength}px`,
    });
  } else if (track.length > maxLength) {
    push({
      code: 'length',
      message: `track too long: ${track.length.toFixed(0)}px > ${maxLength}px`,
    });
  }

  let narrowCount = 0;
  for (let i = 0; i < count; i++) {
    const smp = samples[i] as TrackSample;
    if (!Number.isFinite(smp.x) || !Number.isFinite(smp.y) || !Number.isFinite(smp.halfWidth)) {
      push({ code: 'nonfinite', message: `non-finite sample at index ${i}` });
      return finish(issues);
    }
    if (smp.halfWidth * 2 < opts.minWidth && narrowCount < MAX_ISSUES_PER_KIND) {
      narrowCount++;
      push({
        code: 'narrow',
        s: smp.s,
        message:
          `corridor too narrow at s=${smp.s.toFixed(0)}: ` + `${(smp.halfWidth * 2).toFixed(1)}px`,
      });
      // Skip ahead so one narrow zone reports once, not once per sample.
      i += 24;
    }
  }

  // Curvature: turn radius ≈ step / Δθ between consecutive unit tangents.
  let curvatureCount = 0;
  for (let i = 0; i < count && curvatureCount < MAX_ISSUES_PER_KIND; i++) {
    const a = samples[i] as TrackSample;
    const b = samples[(i + 1) % count] as TrackSample;
    const cross = a.tx * b.ty - a.ty * b.tx;
    const dot = a.tx * b.tx + a.ty * b.ty;
    const dTheta = Math.abs(Math.atan2(cross, dot));
    if (dTheta < 1e-9) continue;
    const radius = track.step / dTheta;
    const needed = a.halfWidth + opts.curvatureMargin;
    if (radius < needed) {
      curvatureCount++;
      push({
        code: 'curvature',
        s: a.s,
        message:
          `turn too tight at s=${a.s.toFixed(0)}: radius ${radius.toFixed(0)}px < ` +
          `halfWidth ${a.halfWidth.toFixed(0)}px + ${opts.curvatureMargin}px (band folds)`,
      });
      i += 24;
    }
  }

  // Self-clearance: O(n²) on ~1000 samples — fine for tests, generation-time
  // checks and the (debounced) editor live validation.
  let clearanceCount = 0;
  outer: for (let i = 0; i < count; i++) {
    const a = samples[i] as TrackSample;
    for (let j = i + 1; j < count; j++) {
      const b = samples[j] as TrackSample;
      const arcSep = Math.abs(forwardArcDelta(a.s, b.s, track.length));
      if (arcSep < opts.minArcSeparation) continue;
      const need = 2 * Math.max(a.halfWidth, b.halfWidth) + opts.clearanceMargin;
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      if (dx * dx + dy * dy < need * need) {
        clearanceCount++;
        push({
          code: 'clearance',
          s: a.s,
          s2: b.s,
          message:
            `clearance violation between s=${a.s.toFixed(0)} and s=${b.s.toFixed(0)}: ` +
            `${Math.sqrt(dx * dx + dy * dy).toFixed(1)}px < ${need.toFixed(1)}px`,
        });
        if (clearanceCount >= MAX_ISSUES_PER_KIND) break outer;
        // One overlapping section is enough to report — skip past it.
        i += 24;
        continue outer;
      }
    }
  }

  return finish(issues);
}

function finish(issues: TrackIssue[]): TrackValidationResult {
  return {
    valid: issues.length === 0,
    errors: issues.map((i) => i.message),
    issues,
  };
}
